import React, { useState } from 'react';
import { Users, ChevronDown, ChevronRight, TrendingUp, Calendar, AlertCircle } from 'lucide-react';

const Team = ({ team, setTeam, onSuccess }) => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [expandedMembers, setExpandedMembers] = useState([]);

  // Sample team data
  const [teamMembers, setTeamMembers] = useState([
    {
      id: 1,
      name: 'Maria Rodriguez',
      department: 'Sales',
      status: 'active',
      onVacation: false,
      vacationEndDate: '',
      outSick: false,
      activeLeads: 12,
      closeRate: 68,
      avgResponseTime: 18,
      specialty: 'Spanish-speaking customers',
      overridesGiven: 3,
      overridesReceived: 1,
      improvementTrend: '+12%'
    },
    {
      id: 2,
      name: 'Carlos Santos',
      department: 'Sales',
      status: 'active',
      onVacation: false,
      vacationEndDate: '',
      outSick: false,
      activeLeads: 8,
      closeRate: 72,
      avgResponseTime: 15,
      specialty: 'High-value deals',
      overridesGiven: 2,
      overridesReceived: 0,
      improvementTrend: '+8%'
    },
    {
      id: 3,
      name: 'Vlad Petrov',
      department: 'Sales',
      status: 'active',
      onVacation: false,
      vacationEndDate: '',
      outSick: false,
      activeLeads: 15,
      closeRate: 61,
      avgResponseTime: 22,
      specialty: 'Russian-speaking customers',
      overridesGiven: 5,
      overridesReceived: 2,
      improvementTrend: '+15%'
    },
    {
      id: 4,
      name: 'Jenny Williams',
      department: 'Service',
      status: 'active',
      onVacation: false,
      vacationEndDate: '',
      outSick: false,
      activeLeads: 20,
      closeRate: 55,
      avgResponseTime: 12,
      specialty: 'Trade-in inspections, AVO processing',
      overridesGiven: 1,
      overridesReceived: 0,
      improvementTrend: '+6%'
    },
    {
      id: 5,
      name: 'Sarah Johnson',
      department: 'F&I',
      status: 'active',
      onVacation: false,
      vacationEndDate: '',
      outSick: false,
      activeLeads: 10,
      closeRate: 82,
      avgResponseTime: 25,
      specialty: 'Financing, accessories packages',
      overridesGiven: 0,
      overridesReceived: 1,
      improvementTrend: '+18%'
    },
    {
      id: 6,
      name: 'Mike Chen',
      department: 'Sales',
      status: 'active',
      onVacation: true,
      vacationEndDate: '2026-01-20',
      outSick: false,
      activeLeads: 0,
      closeRate: 65,
      avgResponseTime: 20,
      specialty: 'Tech-savvy customers',
      overridesGiven: 4,
      overridesReceived: 3,
      improvementTrend: '+10%'
    }
  ]);

  const departments = ['all', 'Sales', 'Service', 'F&I'];

  const filteredMembers = teamMembers.filter(member => 
    selectedDepartment === 'all' || member.department === selectedDepartment
  );

  const toggleExpanded = (memberId) => {
    setExpandedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const updateMemberStatus = (memberId, field, value) => {
  setTeamMembers(prev => prev.map(member => {
    if (member.id === memberId) {
      if (field === 'vacation') {
        return { ...member, onVacation: value, vacationEndDate: value ? member.vacationEndDate : '' };
      } else if (field === 'vacationEndDate') {
        return { ...member, vacationEndDate: value };
      } else if (field === 'sick') {
        return { ...member, outSick: value };
      }
    }
    return member;
  }));
  
  onSuccess(`Updated ${field} for team member`);
};

  return (
    <>
      <div className="header">
        <h1>Team Management</h1>
        <p>Manage team members, availability, and performance</p>
      </div>

      {/* Department Filter */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
          Filter by Department
        </label>
        <select
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
          style={{
            background: '#1a2332',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#FFFFFF',
            fontSize: '14px',
            minWidth: '200px'
          }}
        >
          {departments.map(dept => (
            <option key={dept} value={dept}>
              {dept === 'all' ? 'All Departments' : dept}
            </option>
          ))}
        </select>
      </div>

      {/* Team Members Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
        {filteredMembers.map(member => (
          <div 
            key={member.id}
            style={{
              background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
              border: `1px solid ${member.onVacation || member.outSick ? 'rgba(251, 191, 36, 0.3)' : 'rgba(0, 212, 170, 0.1)'}`,
              borderRadius: '16px',
              overflow: 'hidden'
            }}
          >
            {/* Member Header */}
            <div 
              onClick={() => toggleExpanded(member.id)}
              style={{
                padding: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: expandedMembers.includes(member.id) ? 'rgba(0, 212, 170, 0.05)' : 'transparent'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
                    {member.name}
                  </h3>
                  {member.onVacation && (
                    <span style={{
                      background: 'rgba(251, 191, 36, 0.1)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#fbbf24',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Calendar size={12} />
                      Vacation
                    </span>
                  )}
                  {member.outSick && (
                    <span style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <AlertCircle size={12} />
                      Out Sick
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {member.department}
                </div>
              </div>
              {expandedMembers.includes(member.id) ? (
                <ChevronDown size={20} color="rgba(255, 255, 255, 0.5)" />
              ) : (
                <ChevronRight size={20} color="rgba(255, 255, 255, 0.5)" />
              )}
            </div>

            {/* Member Stats (Always Visible) */}
            <div style={{ padding: '0 20px 20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#00D4AA' }}>
                  {member.activeLeads}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  Active Leads
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#00D4AA' }}>
                  {member.closeRate}%
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  Close Rate
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#00D4AA' }}>
                  {member.avgResponseTime}m
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  Avg Response
                </div>
              </div>
            </div>

            {/* Specialty Badge */}
            <div style={{ padding: '0 20px 20px' }}>
              <div style={{
                background: 'rgba(0, 212, 170, 0.1)',
                border: '1px solid rgba(0, 212, 170, 0.2)',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '13px',
                color: '#00D4AA'
              }}>
                <strong>AI-Assigned Specialty:</strong> {member.specialty}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedMembers.includes(member.id) && (
              <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '20px' }}>
                {/* Performance Details */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#FFFFFF', marginBottom: '12px' }}>
                    Performance Details
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>
                        Overrides Given
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF' }}>
                        {member.overridesGiven}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>
                        Overrides Received
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF' }}>
                        {member.overridesReceived}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>
                        Improvement Trend
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#00D4AA', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <TrendingUp size={16} />
                        {member.improvementTrend}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Edit Attributes */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#FFFFFF', marginBottom: '16px' }}>
                    Edit Attributes
                  </h4>

                  {/* Vacation Toggle */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={member.onVacation}
                        onChange={(e) => updateMemberStatus(member.id, 'vacation', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>On Vacation</span>
                    </label>
                    {member.onVacation && (
                      <div style={{ marginTop: '10px', marginLeft: '28px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '6px' }}>
                          Return Date:
                        </label>
                        <input
                          type="date"
                          value={member.vacationEndDate}
                          onChange={(e) => updateMemberStatus(member.id, 'vacationEndDate', e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            color: '#FFFFFF',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Out Sick Toggle */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={member.outSick}
                        onChange={(e) => updateMemberStatus(member.id, 'sick', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>Out Sick</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default Team;